
import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { X, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { sanitizeTags } from "@/utils/sanitization";

interface TagInputProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxTags?: number;
}

export function TagInput({
  selectedTags = [],
  onTagsChange,
  suggestions = [],
  placeholder = "Add tags...",
  disabled = false,
  className,
  maxTags = 10
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Ensure we have a valid array
  const safeTags = Array.isArray(selectedTags) ? selectedTags : [];
  const safeSuggestions = Array.isArray(suggestions) ? suggestions : [];

  // Filter suggestions based on input and exclude already selected tags
  const filteredSuggestions = safeSuggestions
    .filter(tag => 
      tag.toLowerCase().includes(inputValue.toLowerCase()) && 
      !safeTags.includes(tag)
    )
    .slice(0, 5); // Limit to 5 suggestions

  const addTag = (tag: string) => {
    if (disabled) return;
    
    const trimmedTag = tag.trim().toLowerCase();
    if (!trimmedTag || safeTags.includes(trimmedTag) || safeTags.length >= maxTags) return;
    
    // Sanitize before adding
    const sanitized = sanitizeTags([trimmedTag])[0];
    if (sanitized) {
      onTagsChange([...safeTags, sanitized]);
    }
    
    setInputValue("");
  };

  const removeTag = (tagToRemove: string) => {
    if (disabled) return;
    onTagsChange(safeTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    // Add tag on Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue) addTag(inputValue);
    } 
    // Remove last tag on Backspace if input is empty
    else if (e.key === 'Backspace' && !inputValue && safeTags.length > 0) {
      removeTag(safeTags[safeTags.length - 1]);
    }
    // Hide suggestions on Escape
    else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("w-full space-y-1.5", className)}>
      {/* Tags display */}
      <div className="flex flex-wrap gap-1.5 min-h-[24px]">
        {safeTags.map(tag => (
          <Badge 
            key={tag} 
            variant="secondary"
            className={cn(
              "gap-1.5 py-1 px-2.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100",
              disabled && "opacity-60"
            )}
          >
            {tag}
            {!disabled && (
              <X
                className="h-3 w-3 cursor-pointer text-indigo-400 hover:text-indigo-600"
                onClick={() => removeTag(tag)}
              />
            )}
          </Badge>
        ))}
      </div>

      {/* Input field */}
      <div className="relative">
        <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-indigo-100 focus-within:border-indigo-300 bg-white">
          <Tag className="ml-2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(!!e.target.value);
            }}
            onFocus={() => inputValue && setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            disabled={disabled || safeTags.length >= maxTags}
          />
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div 
            ref={suggestionsRef}
            className="absolute z-50 mt-1 w-full bg-white rounded-md border border-gray-200 shadow-lg max-h-[200px] overflow-auto py-1"
          >
            {filteredSuggestions.map(suggestion => (
              <div
                key={suggestion}
                className="px-3 py-2 cursor-pointer hover:bg-indigo-50 text-sm"
                onClick={() => {
                  addTag(suggestion);
                  setShowSuggestions(false);
                  inputRef.current?.focus();
                }}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Helper text for max tags */}
      {safeTags.length >= maxTags && (
        <p className="text-xs text-muted-foreground mt-1">
          Maximum {maxTags} tags reached
        </p>
      )}
    </div>
  );
}
